import React, { useState, useEffect, useRef } from 'react';
import {
	Box,
	Button,
	Flex,
	Text,
	Icon,
	IconButton,
	VStack,
	HStack,
	Input,
	Textarea,
	Select,
	Badge,
	Spinner,
	useToast,
	useColorModeValue,
	Collapse,
	Divider,
} from '@chakra-ui/react';
import { FaHeadset, FaTimes, FaPaperPlane, FaChevronLeft, FaExternalLinkAlt } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	useMySupportTickets,
	useCreateSupportTicket,
	useAddSupportMessage,
	useSupportTicket,
} from '../../hooks/useSupport';
import { SUPPORT_CATEGORIES } from '../../constants/supportCategories';
import { statusColor, formatStatusLabel } from './supportUtils';

const formatWhen = (date) => {
	if (!date) return '';
	return new Date(date).toLocaleString(undefined, {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
	});
};

const TicketThread = ({ ticketId, onBack }) => {
	const [reply, setReply] = useState('');
	const messagesEndRef = useRef(null);
	const toast = useToast();
	const { data: ticket, isLoading } = useSupportTicket(ticketId, !!ticketId, {
		poll: true,
	});
	const addMessage = useAddSupportMessage();
	const bubbleUser = useColorModeValue('teal.50', 'teal.900');
	const bubbleStaff = useColorModeValue('purple.50', 'purple.900');

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [ticket?.messages?.length]);

	const handleReply = async () => {
		if (!reply.trim()) return;
		try {
			await addMessage.mutateAsync({ id: ticketId, body: reply.trim() });
			setReply('');
			toast({ title: 'Message sent', status: 'success', duration: 3000 });
		} catch (err) {
			toast({
				title: 'Could not send message',
				description: err.response?.data?.error || err.message,
				status: 'error',
				duration: 5000,
			});
		}
	};

	if (isLoading || !ticket) {
		return (
			<Flex justify='center' py={8}>
				<Spinner color='teal.500' />
			</Flex>
		);
	}

	return (
		<VStack align='stretch' spacing={3} h='full'>
			<HStack>
				<IconButton
					aria-label='Back'
					icon={<FaChevronLeft />}
					size='sm'
					variant='ghost'
					onClick={onBack}
				/>
				<Box flex={1} minW={0}>
					<Text fontWeight='bold' fontSize='sm' noOfLines={1}>
						{ticket.subject}
					</Text>
					<HStack spacing={2} mt={0.5}>
						<Text fontSize='xs' color='gray.500'>
							{ticket.ticketNumber}
						</Text>
						<Badge colorScheme={statusColor(ticket.status)} borderRadius='full' fontSize='10px'>
							{formatStatusLabel(ticket.status, 'customer')}
						</Badge>
					</HStack>
				</Box>
			</HStack>

			<Box flex={1} overflowY='auto' maxH='280px' pr={1}>
				<VStack align='stretch' spacing={3}>
					{(ticket.messages || []).map((msg, idx) => (
						<Box
							key={idx}
							alignSelf={msg.isStaffReply ? 'flex-start' : 'flex-end'}
							maxW='92%'
							bg={msg.isStaffReply ? bubbleStaff : bubbleUser}
							borderRadius='xl'
							px={3}
							py={2}>
							<Text fontSize='10px' fontWeight='bold' color='gray.500' mb={0.5}>
								{msg.isStaffReply ? 'Support team' : 'You'} · {formatWhen(msg.createdAt)}
							</Text>
							<Text fontSize='sm' whiteSpace='pre-wrap'>
								{msg.body}
							</Text>
						</Box>
					))}
					<Box ref={messagesEndRef} />
				</VStack>
			</Box>

			{ticket.status !== 'closed' && (
				<VStack align='stretch' spacing={2}>
					<Textarea
						size='sm'
						placeholder='Write a reply...'
						value={reply}
						onChange={(e) => setReply(e.target.value)}
						borderRadius='xl'
						rows={3}
						resize='none'
					/>
					<Button
						size='sm'
						colorScheme='teal'
						leftIcon={<FaPaperPlane />}
						borderRadius='xl'
						isLoading={addMessage.isPending}
						onClick={handleReply}
						isDisabled={!reply.trim()}>
						Send reply
					</Button>
				</VStack>
			)}
		</VStack>
	);
};

const NewTicketForm = ({ onCreated }) => {
	const [subject, setSubject] = useState('');
	const [category, setCategory] = useState('general');
	const [message, setMessage] = useState('');
	const createTicket = useCreateSupportTicket();
	const toast = useToast();

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!subject.trim() || !message.trim()) return;

		try {
			const result = await createTicket.mutateAsync({
				subject: subject.trim(),
				category,
				message: message.trim(),
			});
			toast({
				title: 'Support request sent',
				description: `Ticket ${result.ticket?.ticketNumber || ''} created. We'll get back to you soon.`,
				status: 'success',
				duration: 5000,
			});
			setSubject('');
			setMessage('');
			onCreated(result.ticket?._id);
		} catch (err) {
			toast({
				title: 'Could not send request',
				description: err.response?.data?.error || err.message,
				status: 'error',
				duration: 5000,
			});
		}
	};

	return (
		<Box as='form' onSubmit={handleSubmit}>
			<VStack spacing={3} align='stretch'>
				<Input
					placeholder='Subject'
					value={subject}
					onChange={(e) => setSubject(e.target.value)}
					size='sm'
					borderRadius='xl'
					required
				/>
				<Select
					size='sm'
					value={category}
					onChange={(e) => setCategory(e.target.value)}
					borderRadius='xl'>
					{SUPPORT_CATEGORIES.map((c) => (
						<option key={c.value} value={c.value}>
							{c.label}
						</option>
					))}
				</Select>
				<Textarea
					placeholder='How can we help?'
					value={message}
					onChange={(e) => setMessage(e.target.value)}
					size='sm'
					borderRadius='xl'
					rows={4}
					resize='none'
					required
				/>
				<Button
					type='submit'
					colorScheme='teal'
					size='sm'
					leftIcon={<FaPaperPlane />}
					borderRadius='xl'
					isLoading={createTicket.isPending}
					isDisabled={!subject.trim() || !message.trim()}>
					Send to support
				</Button>
			</VStack>
		</Box>
	);
};

const SupportWidget = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [view, setView] = useState('home');
	const [selectedTicketId, setSelectedTicketId] = useState(null);
	const location = useLocation();
	const navigate = useNavigate();
	const showWidget =
		location.pathname !== '/support' && location.pathname !== '/my-support';

	const { data: myTicketsData, isLoading } = useMySupportTickets(showWidget, {
		// Only poll while the panel is open; when closed, rely on the initial fetch
		// and refetch-on-window-focus instead of hitting the API every 8 seconds.
		poll: showWidget && isOpen,
	});
	const myTickets = myTicketsData?.tickets ?? [];
	const unreadCount = myTicketsData?.totalUnread ?? 0;

	const panelBg = useColorModeValue('white', 'gray.800');
	const panelBorder = useColorModeValue('gray.200', 'gray.700');
	const launcherBg = useColorModeValue('teal.500', 'teal.400');

	if (!showWidget) {
		return null;
	}

	const openTickets = myTickets.filter((t) => !['closed', 'resolved'].includes(t.status));

	return (
		<Box
			position='fixed'
			right={{ base: 2, md: 2 }}
			bottom='10px'
			transform='translateY(3%)'
			zIndex={1400}
			display='flex'
			flexDirection='row-reverse'
			alignItems='center'
			gap={3}>
			<Collapse in={isOpen} animateOpacity unmountOnExit >
				<Box
					w={{ base: 'min(92vw, 360px)', md: '380px' }}
					// h='500px'
					bg={panelBg}
					border='1px solid'
					borderColor={panelBorder}
					borderRadius='2xl'
					boxShadow='2xl'
					overflow='hidden'>
					<Flex
						bgGradient='linear(to-r, teal.500, blue.500)'
						color='white'
						px={4}
						py={3}
						align='center'
						justify='space-between'>
						<HStack spacing={2}>
							<Icon as={FaHeadset} />
							<Box>
								<Text fontWeight='bold' fontSize='sm' mb={0}>
									Need help?
								</Text>
								<Text fontSize='xs' opacity={0.9} mb={0}>
									We typically reply less than 20 minutes
								</Text>
							</Box>
						</HStack>
						<IconButton
							aria-label='Close support'
							icon={<FaTimes />}
							size='sm'
							variant='ghost'
							color='white'
							_hover={{ bg: 'whiteAlpha.200' }}
							onClick={() => {
								setIsOpen(false);
								setView('home');
								setSelectedTicketId(null);
							}}
						/>
					</Flex>

					<Box p={4} minH='320px' maxH='480px' overflow='hidden'>
						{view === 'thread' && selectedTicketId ? (
							<TicketThread
								ticketId={selectedTicketId}
								onBack={() => {
									setView('home');
									setSelectedTicketId(null);
								}}
							/>
						) : (
							<VStack align='stretch' spacing={4}>
								{openTickets.length > 0 && (
									<Box>
										<Flex justify='space-between' align='center' mb={2}>
											<Text fontSize='xs' fontWeight='bold' color='gray.500' textTransform='uppercase'>
												Your open tickets
											</Text>
											<Button
												size='xs'
												variant='link'
												colorScheme='teal'
												rightIcon={<FaExternalLinkAlt />}
												onClick={() => {
													setIsOpen(false);
													navigate('/my-support');
												}}>
												View all
											</Button>
										</Flex>
										<VStack align='stretch' spacing={2}>
											{openTickets.slice(0, 3).map((ticket) => (
												<Button
													key={ticket._id}
													variant='outline'
													size='sm'
													justifyContent='flex-start'
													borderRadius='xl'
													h='auto'
													py={2}
													position='relative'
													onClick={() => {
														setSelectedTicketId(ticket._id);
														setView('thread');
													}}>
													<Box textAlign='left' w='full'>
														<Flex justify='space-between' align='center' gap={2}>
															<Text fontSize='sm' fontWeight='semibold' noOfLines={1} flex={1}>
																{ticket.subject}
															</Text>
															{ticket.unreadCount > 0 && (
																<Badge
																	colorScheme='red'
																	borderRadius='full'
																	fontSize='10px'
																	minW='18px'
																	textAlign='center'>
																	{ticket.unreadCount}
																</Badge>
															)}
														</Flex>
														<Text fontSize='xs' color='gray.500'>
															{ticket.ticketNumber} · {formatWhen(ticket.lastMessageAt)}
														</Text>
													</Box>
												</Button>
											))}
										</VStack>
										<Divider my={3} />
									</Box>
								)}

								<Box>
									<Flex justify='space-between' align='center' mb={2}>
										<Text fontSize='xs' fontWeight='bold' color='gray.500' textTransform='uppercase'>
											New request
										</Text>
										<Button
											size='xs'
											variant='link'
											colorScheme='teal'
											rightIcon={<FaExternalLinkAlt />}
											onClick={() => {
												setIsOpen(false);
												navigate('/my-support');
											}}>
											Full history
										</Button>
									</Flex>
									<NewTicketForm
										onCreated={(id) => {
											if (id) {
												setSelectedTicketId(id);
												setView('thread');
											}
										}}
									/>
								</Box>

								{isLoading && (
									<Flex justify='center'>
										<Spinner size='sm' color='teal.500' />
									</Flex>
								)}
							</VStack>
						)}
					</Box>
				</Box>
			</Collapse>

			<Box position='relative'>
				<Button
					aria-label={isOpen ? 'Close support' : 'Contact support'}
					onClick={() => setIsOpen((v) => !v)}
					borderRadius='full'
					w='56px'
					h='56px'
					minW='56px'
					p={0}
					bg={launcherBg}
					color='white'
					boxShadow='lg'
					_hover={{ transform: 'scale(1.05)', bg: 'teal.600' }}
					transition='all 0.2s'>
					<Icon as={isOpen ? FaTimes : FaHeadset} boxSize={5} />
				</Button>
				{!isOpen && unreadCount > 0 && (
					<Badge
						position='absolute'
						top='-2px'
						right='-2px'
						borderRadius='full'
						colorScheme='red'
						fontSize='10px'
						minW='20px'
						h='20px'
						display='flex'
						alignItems='center'
						justifyContent='center'
						px={1}
						boxShadow='md'>
						{unreadCount > 9 ? '9+' : unreadCount}
					</Badge>
				)}
			</Box>
		</Box>
	);
};

export default SupportWidget;
